// Платформенный слой доставки обновлений веб-слоя.
//
// Две реализации за одним контрактом:
//   - Android (Capacitor): `@capgo/capacitor-updater` — бандл едет в APK, поверх
//     догружается новый (`offline-first.md` §2);
//   - Electron: мост оболочки `window.restomarkerShell.webUpdate` — то же
//     проверить-скачать-применить, файловую работу делает главный процесс
//     (`desktop/src/webUpdate.ts`).
// Вне оболочек (dev в браузере, vitest, прод-сборка веба) — no-op.
//
// Выше по стеку (`checkForUpdate`, стор, хук, баннер) платформы не видно вовсе.

import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import { desktopShellBridge, isDesktopShell } from '@/shared/lib/shell';

const shellWebUpdate = () => desktopShellBridge()?.webUpdate ?? null;

/** Обновление веб-слоя работает в нативной оболочке и в Electron. В браузере — no-op. */
export function updatesSupported(): boolean {
  return Capacitor.isNativePlatform() || isDesktopShell();
}

/**
 * Сообщить, что текущий бандл поднялся без ошибок.
 *
 * И на Android, и в Electron это страховка от нерабочего обновления: не позвали за
 * запуск — на следующем старте платформа откатывается на предыдущий бандл. Забыть
 * позвать отсюда на старте — значит превратить рабочий бандл в откат.
 */
export async function notifyReady(): Promise<void> {
  try {
    if (isDesktopShell()) {
      await shellWebUpdate()?.confirm();
      return;
    }
    if (!Capacitor.isNativePlatform()) return;
    await CapacitorUpdater.notifyAppReady();
  } catch (error) {
    console.warn('appUpdate: notifyReady не прошёл', error);
  }
}

/** Версия работающего сейчас бандла. */
export async function currentVersion(): Promise<string | null> {
  try {
    if (isDesktopShell()) {
      return (await shellWebUpdate()?.current()) ?? null;
    }
    if (!Capacitor.isNativePlatform()) return null;
    const { bundle, native } = await CapacitorUpdater.current();
    // `builtin` — псевдо-версия встроенного бандла у плагина; для сравнения берём
    // нативную версию APK (versionName из package.json — тот же источник, что на сервере).
    return bundle.version && bundle.version !== 'builtin' ? bundle.version : native;
  } catch (error) {
    console.warn('appUpdate: current() не прошёл', error);
    return null;
  }
}

/**
 * Версия самой оболочки — APK или Electron, в браузере `null`.
 *
 * Не путать с `currentVersion()`: та — версия веб-слоя, который обновляется по воздуху,
 * а оболочка меняется только переустановкой, и её отставание видно только здесь.
 */
export async function shellVersion(): Promise<string | null> {
  try {
    if (isDesktopShell()) {
      return (await desktopShellBridge()?.shellVersion?.()) ?? null;
    }
    if (!Capacitor.isNativePlatform()) return null;
    const { native } = await CapacitorUpdater.current();
    return native || null;
  } catch (error) {
    console.warn('appUpdate: версия оболочки не получена', error);
    return null;
  }
}

export type DownloadedBundle = { readonly id: string; readonly version: string };

/** Скачать бандл. Платформа сама сверяет sha256 и не активирует бандл при расхождении. */
export async function downloadBundle(args: {
  url: string;
  version: string;
  checksum: string;
}): Promise<DownloadedBundle> {
  if (isDesktopShell()) {
    const bridge = shellWebUpdate();
    if (!bridge) {
      throw new Error('мост обновления недоступен');
    }
    const result = await bridge.download(args);
    if (!result.ok) {
      throw new Error(`загрузка бандла не удалась: ${result.error}`);
    }
    return { id: result.id, version: args.version };
  }

  const info = await CapacitorUpdater.download(args);
  return { id: info.id, version: info.version };
}

/**
 * Применить скачанный бандл и перезагрузить webview.
 *
 * Вызывается ТОЛЬКО по явному действию пользователя (кнопка в баннере): перезагрузка
 * посреди печати недопустима (`offline-first.md` §2), а нажатие на кнопку — это
 * согласие, которого посреди печати не бывает.
 */
export async function applyBundle(id: string): Promise<void> {
  if (isDesktopShell()) {
    // Главный процесс сам перезагрузит окно после подмены каталога.
    await shellWebUpdate()?.apply(id);
    return;
  }
  await CapacitorUpdater.set({ id });
  await CapacitorUpdater.reload();
}
